
import { defineComponent } from '@nuxtjs/composition-api';
import { buildQuery, isExternalReferer } from '~/components/utils/browser/url';
import { SIGN_UP_MODAL, SIGN_IN_MODAL, COOKIE_NAME_REDIRECT_TO_ONBOARDING } from '~/constants';
import { IS_SELECTED_JOBS_PRODUCT } from '~/components/partials/jobs/constants';
import { NoticeMarkup } from '~/components/common/Notice/types';

const AuthModals = [SIGN_IN_MODAL, SIGN_UP_MODAL];
const SIGNIN_QUERY_PARAMS = ['dynamic_modal', 'referer', 'refferer'];
const isAuthModal = (modal: string) => AuthModals.includes(modal);

const withTracking = (referer: string, event: string, queryParams: Record<string, unknown>) => {
  const query = {
    ...(event && { event }),
    ...queryParams,
    referer,
  };

  if (isExternalReferer(referer)) {
    return `/redirect?${buildQuery(query)}`;
  }

  let basePath = referer;
  if (basePath.startsWith('/')) {
    basePath = basePath.substring(1);
  }

  for (const qparams of SIGNIN_QUERY_PARAMS) {
    if (queryParams[qparams]) delete queryParams[qparams];
  }

  return `/${basePath}${basePath.endsWith('?') ? '&' : '?'}${buildQuery({
    ...(event && { event }),
    ...queryParams,
  })}`;
};

export default defineComponent({
  name: 'LoginExternal',
  layout: 'clear',
  middleware: [
    function redirectJobsApplicationToOnboarding({ query, redirect }) {
      const isJobsApplication = Object.keys(query).includes('vacancy_uid');

      if (isJobsApplication) {
        return redirect('/b2c/', query);
      }
    },
    function checkAuthAndRedirect({ route, query, redirect, app }) {
      const event = query.event as string;
      // Fix typo
      const referer = (query.referer || query.refferer) as string;
      const withOnboardingRedirect = route.fullPath?.includes('onboarding');

      // We add the cookie to trigger jobs onboarding if user comes from jobs as iframe(bravado.co/landing/sales-jobs)
      query.jobs_iframe && app.$cookies.set(IS_SELECTED_JOBS_PRODUCT, true);
      withOnboardingRedirect && app.$cookies.set(COOKIE_NAME_REDIRECT_TO_ONBOARDING, true);

      if (app.$auth.loggedIn) {
        // redirect users with onboarding=true to onboarding page regardless of completion
        if (withOnboardingRedirect) {
          return redirect('/b2c/', query);
        }
        // redirects users to Xperiencify profile if they came from Tech Sales U landing
        if (app.$auth.user.xperiencifyMagicLoginLink && query.product === 'TECH_SALES_U_PRODUCT') {
          return redirect(app.$auth.user.xperiencifyMagicLoginLink);
        }

        const KNOWN_OUTSIDE_REFERERS = ['salaries'];

        if (referer && KNOWN_OUTSIDE_REFERERS.some(ref => referer.includes(ref)) && process.client) {
          window.location.href = referer;
          return;
        }

        return redirect(referer ? withTracking(referer, event, query) : '/');
      }
    },
  ],
  computed: {
    noticeText(): string | null {
      switch (this.$route.query.error) {
        case 'deactivated':
          return "Your account is blocked / suspended, please <a style='text-decoration: underline' href='/war-room?dynamic_modal=contact-us'>contact our support</a>.";
        default:
          return null;
      }
    },
  },
  watch: {
    $route(to) {
      const dynamicModal = to.query.dynamic_modal as string;
      const modalName = isAuthModal(dynamicModal as string) ? dynamicModal : SIGN_UP_MODAL;
      this.$modal.showWithRoute(modalName);
    },
  },
  mounted() {
    const dynamicModal = this.$route?.query.dynamic_modal as string;
    const modalName = isAuthModal(dynamicModal as string) ? dynamicModal : SIGN_UP_MODAL;
    this.$3rdParty.loadImmediately(['tiktok', 'facebook', 'linkedin']);
    this.$modal.showWithRoute(modalName);

    if (this.noticeText) {
      this.$notice.error(this.noticeText, undefined, NoticeMarkup.HTML);
    }
  },
});
